/**
 * Icon shim — single source of truth for icon imports across the app.
 *
 * Every icon historically imported from `lucide-react` is re-exported here
 * under the same name, but backed by `@tabler/icons-react`. JSX usage is
 * unchanged across the codebase; only the import path differs.
 *
 * Why a shim?
 *   • Tabler uses an "Icon" prefix (e.g. `IconAlertCircle`); Lucide does
 *     not (e.g. `AlertCircle`). Re-exporting under Lucide names avoids a
 *     codebase-wide JSX rename.
 *   • A thin wrapper normalizes prop names so existing call sites work:
 *     Lucide's `strokeWidth` is mapped to Tabler's `stroke`, and a default
 *     stroke of 1.75 is applied for the sleeker Linear/Raycast register.
 */
import * as React from "react";
import {
  type IconProps,
  IconAccessible,
  IconAlertCircle,
  IconAlertTriangle,
  IconArrowBackUp,
  IconArrowBarToDown,
  IconArrowBarUp,
  IconArrowForwardUp,
  IconArrowLeft,
  IconArrowRight,
  IconArrowsLeftRight,
  IconArrowsShuffle,
  IconAtom,
  IconAtom2,
  IconAward,
  IconAdjustments,
  IconBattery,
  IconBolt,
  IconBook2,
  IconBookmark,
  IconBookmarkPlus,
  IconBoxMultiple,
  IconBrandFacebook,
  IconBrandInstagram,
  IconBuildingBank,
  IconBulb,
  IconCalculator,
  IconCalendar,
  IconCalendarEvent,
  IconCheck,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconCircle,
  IconCircleCheck,
  IconCircleX,
  IconClock,
  IconColumns2,
  IconCompass,
  IconCopy,
  IconDatabase,
  IconDeviceFloppy,
  IconDeviceGamepad2,
  IconDots,
  IconDownload,
  IconDroplet,
  IconDroplets,
  IconEraser,
  IconExternalLink,
  IconEye,
  IconEyeOff,
  IconFileDownload,
  IconFileText,
  IconFlag,
  IconFlame,
  IconFlask,
  IconGauge,
  IconGripVertical,
  IconGrid3x3,
  IconH1,
  IconH2,
  IconH3,
  IconHandStop,
  IconHeart,
  IconHelpCircle,
  IconHexagon,
  IconHistory,
  IconInfoCircle,
  IconKeyboard,
  IconLanguage,
  IconLayoutGrid,
  IconLayoutSidebar,
  IconLink,
  IconListCheck,
  IconListNumbers,
  IconListTree,
  IconLoader2,
  IconLock,
  IconLogout,
  IconMaximize,
  IconMenu2,
  IconMinus,
  IconMoon,
  IconNavigation,
  IconNotebook,
  IconNotes,
  IconPhotoOff,
  IconPlayerPause,
  IconPlayerPlay,
  IconPlus,
  IconPoint,
  IconPointer,
  IconQrcode,
  IconRadioactive,
  IconRefresh,
  IconRepeat,
  IconRotate,
  IconRotateClockwise,
  IconRuler,
  IconScale,
  IconSchool,
  IconSearch,
  IconSettings,
  IconShare,
  IconShieldCheck,
  IconShieldX,
  IconSkull,
  IconSnowflake,
  IconSparkles,
  IconSquare,
  IconStack2,
  IconStopwatch,
  IconSun,
  IconSword,
  IconSwords,
  IconTable,
  IconTag,
  IconTemperature,
  IconThumbDown,
  IconThumbUp,
  IconTool,
  IconTrash,
  IconTrophy,
  IconTypography,
  IconUser,
  IconUsers,
  IconVolume,
  IconVolumeOff,
  IconWaveSine,
  IconWind,
  IconWorld,
  IconX,
  IconZoomIn,
  IconZoomOut,
} from "@tabler/icons-react";

/** Permissive icon component type — drop-in replacement for Lucide's `LucideIcon`. */
export type LucideIcon = React.ForwardRefExoticComponent<
  Omit<IconProps & { strokeWidth?: number | string }, "ref"> &
    React.RefAttributes<SVGSVGElement>
>;

/**
 * Wraps a Tabler icon so it accepts Lucide's `strokeWidth` prop and applies
 * a default stroke of 1.75 for a sleeker, more refined look at small sizes.
 */
function wrap(
  Cmp: React.ForwardRefExoticComponent<
    IconProps & React.RefAttributes<SVGSVGElement>
  >,
  displayName: string
): LucideIcon {
  const Wrapped = React.forwardRef<
    SVGSVGElement,
    IconProps & { strokeWidth?: number | string }
  >(({ strokeWidth, stroke, ...rest }, ref) => (
    <Cmp ref={ref} stroke={stroke ?? strokeWidth ?? 1.75} {...rest} />
  ));
  Wrapped.displayName = displayName;
  return Wrapped as LucideIcon;
}

/* ── Re-exports under Lucide names ─────────────────────────────── */

export const Accessibility = wrap(IconAccessible, "Accessibility");
export const AlertCircle = wrap(IconAlertCircle, "AlertCircle");
export const AlertTriangle = wrap(IconAlertTriangle, "AlertTriangle");
export const ArrowDownToLine = wrap(IconArrowBarToDown, "ArrowDownToLine");
export const ArrowLeft = wrap(IconArrowLeft, "ArrowLeft");
export const ArrowLeftRight = wrap(IconArrowsLeftRight, "ArrowLeftRight");
export const ArrowRight = wrap(IconArrowRight, "ArrowRight");
export const ArrowUpFromLine = wrap(IconArrowBarUp, "ArrowUpFromLine");
export const Atom = wrap(IconAtom, "Atom");
export const Award = wrap(IconAward, "Award");
export const Battery = wrap(IconBattery, "Battery");
export const Beaker = wrap(IconFlask, "Beaker");
export const BookMarked = wrap(IconBookmark, "BookMarked");
export const BookOpen = wrap(IconBook2, "BookOpen");
export const BookmarkPlus = wrap(IconBookmarkPlus, "BookmarkPlus");
export const Boxes = wrap(IconBoxMultiple, "Boxes");
export const Calculator = wrap(IconCalculator, "Calculator");
export const Calendar = wrap(IconCalendar, "Calendar");
export const CalendarDays = wrap(IconCalendarEvent, "CalendarDays");
export const Check = wrap(IconCheck, "Check");
export const CheckCircle2 = wrap(IconCircleCheck, "CheckCircle2");
export const ChevronDown = wrap(IconChevronDown, "ChevronDown");
export const ChevronLeft = wrap(IconChevronLeft, "ChevronLeft");
export const ChevronRight = wrap(IconChevronRight, "ChevronRight");
export const ChevronUp = wrap(IconChevronUp, "ChevronUp");
export const Circle = wrap(IconCircle, "Circle");
export const Clock = wrap(IconClock, "Clock");
export const Columns2 = wrap(IconColumns2, "Columns2");
export const Compass = wrap(IconCompass, "Compass");
export const Copy = wrap(IconCopy, "Copy");
export const Database = wrap(IconDatabase, "Database");
export const Dot = wrap(IconPoint, "Dot");
export const Download = wrap(IconDownload, "Download");
export const Droplet = wrap(IconDroplet, "Droplet");
export const Droplets = wrap(IconDroplets, "Droplets");
export const Eraser = wrap(IconEraser, "Eraser");
export const ExternalLink = wrap(IconExternalLink, "ExternalLink");
export const Eye = wrap(IconEye, "Eye");
export const EyeOff = wrap(IconEyeOff, "EyeOff");
export const Facebook = wrap(IconBrandFacebook, "Facebook");
export const FileDown = wrap(IconFileDownload, "FileDown");
export const FileText = wrap(IconFileText, "FileText");
export const Flag = wrap(IconFlag, "Flag");
export const Flame = wrap(IconFlame, "Flame");
export const FlaskConical = wrap(IconFlask, "FlaskConical");
export const Gamepad2 = wrap(IconDeviceGamepad2, "Gamepad2");
export const Gauge = wrap(IconGauge, "Gauge");
export const Globe2 = wrap(IconWorld, "Globe2");
export const GraduationCap = wrap(IconSchool, "GraduationCap");
export const Grid3x3 = wrap(IconGrid3x3, "Grid3x3");
export const GripVertical = wrap(IconGripVertical, "GripVertical");
export const Handshake = wrap(IconHandStop, "Handshake");
export const Heading1 = wrap(IconH1, "Heading1");
export const Heading2 = wrap(IconH2, "Heading2");
export const Heading3 = wrap(IconH3, "Heading3");
export const Heart = wrap(IconHeart, "Heart");
export const HelpCircle = wrap(IconHelpCircle, "HelpCircle");
export const Hexagon = wrap(IconHexagon, "Hexagon");
export const History = wrap(IconHistory, "History");
export const ImageOff = wrap(IconPhotoOff, "ImageOff");
export const Info = wrap(IconInfoCircle, "Info");
export const Instagram = wrap(IconBrandInstagram, "Instagram");
export const Keyboard = wrap(IconKeyboard, "Keyboard");
export const Landmark = wrap(IconBuildingBank, "Landmark");
export const Languages = wrap(IconLanguage, "Languages");
export const Layers = wrap(IconStack2, "Layers");
export const LayoutGrid = wrap(IconLayoutGrid, "LayoutGrid");
export const Lightbulb = wrap(IconBulb, "Lightbulb");
export const Link = wrap(IconLink, "Link");
export const Link2 = wrap(IconLink, "Link2");
export const ListChecks = wrap(IconListCheck, "ListChecks");
export const ListOrdered = wrap(IconListNumbers, "ListOrdered");
export const ListTree = wrap(IconListTree, "ListTree");
export const Loader2 = wrap(IconLoader2, "Loader2");
export const Lock = wrap(IconLock, "Lock");
export const LogOut = wrap(IconLogout, "LogOut");
export const Maximize2 = wrap(IconMaximize, "Maximize2");
export const Menu = wrap(IconMenu2, "Menu");
export const Minus = wrap(IconMinus, "Minus");
export const Moon = wrap(IconMoon, "Moon");
export const MoreHorizontal = wrap(IconDots, "MoreHorizontal");
export const MousePointer2 = wrap(IconPointer, "MousePointer2");
export const Navigation = wrap(IconNavigation, "Navigation");
export const Notebook = wrap(IconNotebook, "Notebook");
export const NotebookPen = wrap(IconNotes, "NotebookPen");
export const Orbit = wrap(IconAtom2, "Orbit");
export const PanelLeft = wrap(IconLayoutSidebar, "PanelLeft");
export const Pause = wrap(IconPlayerPause, "Pause");
export const Play = wrap(IconPlayerPlay, "Play");
export const Plus = wrap(IconPlus, "Plus");
export const QrCode = wrap(IconQrcode, "QrCode");
export const Radiation = wrap(IconRadioactive, "Radiation");
export const Redo2 = wrap(IconArrowForwardUp, "Redo2");
export const RefreshCcw = wrap(IconRefresh, "RefreshCcw");
export const RefreshCw = wrap(IconRefresh, "RefreshCw");
export const Repeat = wrap(IconRepeat, "Repeat");
export const RotateCcw = wrap(IconRotate, "RotateCcw");
export const RotateCw = wrap(IconRotateClockwise, "RotateCw");
export const Ruler = wrap(IconRuler, "Ruler");
export const Save = wrap(IconDeviceFloppy, "Save");
export const Scale = wrap(IconScale, "Scale");
export const Search = wrap(IconSearch, "Search");
export const Settings = wrap(IconSettings, "Settings");
export const Settings2 = wrap(IconAdjustments, "Settings2");
export const Share2 = wrap(IconShare, "Share2");
export const ShieldAlert = wrap(IconShieldX, "ShieldAlert");
export const ShieldCheck = wrap(IconShieldCheck, "ShieldCheck");
export const Shuffle = wrap(IconArrowsShuffle, "Shuffle");
export const Skull = wrap(IconSkull, "Skull");
export const Snowflake = wrap(IconSnowflake, "Snowflake");
export const Sparkles = wrap(IconSparkles, "Sparkles");
export const Square = wrap(IconSquare, "Square");
export const Sun = wrap(IconSun, "Sun");
export const Sword = wrap(IconSword, "Sword");
export const Swords = wrap(IconSwords, "Swords");
export const Table = wrap(IconTable, "Table");
export const Tag = wrap(IconTag, "Tag");
export const Thermometer = wrap(IconTemperature, "Thermometer");
export const ThumbsDown = wrap(IconThumbDown, "ThumbsDown");
export const ThumbsUp = wrap(IconThumbUp, "ThumbsUp");
export const Timer = wrap(IconStopwatch, "Timer");
export const Trash2 = wrap(IconTrash, "Trash2");
export const Trophy = wrap(IconTrophy, "Trophy");
export const Type = wrap(IconTypography, "Type");
export const Undo2 = wrap(IconArrowBackUp, "Undo2");
export const User = wrap(IconUser, "User");
export const Users = wrap(IconUsers, "Users");
export const Volume2 = wrap(IconVolume, "Volume2");
export const VolumeX = wrap(IconVolumeOff, "VolumeX");
export const Waves = wrap(IconWaveSine, "Waves");
export const Wind = wrap(IconWind, "Wind");
export const Wrench = wrap(IconTool, "Wrench");
export const X = wrap(IconX, "X");
export const XCircle = wrap(IconCircleX, "XCircle");
export const Zap = wrap(IconBolt, "Zap");
export const ZoomIn = wrap(IconZoomIn, "ZoomIn");
export const ZoomOut = wrap(IconZoomOut, "ZoomOut");